/**
 * Ad-platform sign-up conversion (#994).
 *
 * The Google Ads and Microsoft Ads sign-up conversions used to be page-path
 * rules on `/register/success`. Quick signup (#943) never reaches that page,
 * so both counted nothing. This fires an explicit `sign_up` event instead:
 *
 * - `window.dataLayer` for Google Tag Manager (GTM-W8ZLBHHT), whose Custom
 *   Event trigger `sign_up` fires the Google Ads Sign-up conversion;
 * - `window.uetq` for Microsoft UET (tag 187280481), whose Event goal with
 *   action `sign_up` counts it.
 *
 * Both are plain queues. GTM, and UET through it, load only after marketing
 * consent; an event pushed before then waits in the queue and is sent if the
 * visitor consents later in the same session, and is never sent otherwise.
 */

/** Event name shared by the GTM trigger and the Microsoft Ads goal. */
export const SIGNUP_CONVERSION_EVENT = 'sign_up';

/** How the account was created. */
export type SignupMethod = 'email' | 'sso';

type QueueWindow = Window & { dataLayer?: unknown[]; uetq?: unknown[] };

/**
 * Records a completed signup for the ad platforms. Call once, when the new
 * company exists. Never throws: tracking must not break signup.
 *
 * @param method How the account was created.
 * @param win The browser window (injectable for tests).
 */
export function trackSignupConversion(method: SignupMethod, win: QueueWindow = window): void {
  // Each queue is isolated: a blocked or replaced Google queue must not cost
  // the Microsoft conversion, or the other way round. Neither failure affects
  // the signup itself.
  try {
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ event: SIGNUP_CONVERSION_EVENT, method });
  } catch {
    // GTM queue unavailable.
  }
  try {
    win.uetq = win.uetq || [];
    win.uetq.push('event', SIGNUP_CONVERSION_EVENT, { event_label: method });
  } catch {
    // UET queue unavailable.
  }
}

/**
 * How a signed-in account was created, from its Firebase provider ids. A
 * `password` provider means an email signup; any other provider is single
 * sign-on. Used where the flow alone does not say: the company-only form also
 * serves an email signup resuming after its company creation failed.
 *
 * @param providerIds Firebase `providerData[].providerId` values.
 */
export function signupMethodFromProviders(providerIds: readonly string[]): SignupMethod {
  return providerIds.includes('password') ? 'email' : 'sso';
}
